import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { LottieSpinner } from '@/components/LottieSpinner/LottieSpinner'
import { Button } from '@/components/ui/button'
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/ui/toggle-group'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import {
  EUserPermission,
  EUserRole,
} from '@/hooks/usePermissions/permissions.type'
import { usePermissions } from '@/hooks/usePermissions/usePermissions'
import { CustomizeReportSheet } from '@/pages/RunReport/components/CustomizeReportSheet'
import { useUpdateEnterpriseConfigMutation } from '@/store/api/Enterprise/Enterprise.query'
import {
  useUpdateUserPdfConfigMutation,
  useUserInfoQuery,
} from '@/store/api/Users/Users.query'
import type { customizeReportSchemaType } from '@/utils/schemas'

const RETENTION_OPTIONS = [30, 45, 60, 120, 180] as const
const DEFAULT_RETENTION = 180

const LANGUAGES = [
  { code: 'en', labelKey: 'dashboard.account.english' },
  { code: 'es', labelKey: 'dashboard.account.spanish' },
] as const

const buildSections = (
  config?: Partial<customizeReportSchemaType>,
): customizeReportSchemaType => ({
  pdfLanguage: config?.pdfLanguage ?? 'en',
  pdfCoreFindings: config?.pdfCoreFindings ?? false,
  pdfSocialMediaAccounts: config?.pdfSocialMediaAccounts ?? false,
  pdfSentimentAnalysis: config?.pdfSentimentAnalysis ?? false,
  pdfFlaggedBehaviors: config?.pdfFlaggedBehaviors ?? false,
  pdfPositivePosts: config?.pdfPositivePosts ?? false,
  pdfNeutralPosts: config?.pdfNeutralPosts ?? false,
  pdfNegativePosts: config?.pdfNegativePosts ?? false,
  pdfWebSearchPosts: config?.pdfWebSearchPosts ?? false,
  pdfPersonalityAndSkills: config?.pdfPersonalityAndSkills ?? false,
  pdfEducationAndCareer: config?.pdfEducationAndCareer ?? false,
  pdfDigitalFootprint: config?.pdfDigitalFootprint ?? false,
  pdfContentRadar: config?.pdfContentRadar ?? false,
  pdfUnverifiedDigitalFootprint: config?.pdfUnverifiedDigitalFootprint ?? false,
})

export const ReportSettings = () => {
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const canEditRetention = hasPermission(
    EUserPermission.HANDLE_ENTERPRISES,
    EUserRole.OWNER,
  )

  const { data, isLoading } = useUserInfoQuery()

  const initialSections = useMemo(
    () => buildSections(data?.pdfConfig),
    [data?.pdfConfig],
  )
  const initialRetention =
    data?.enterprise?.enterpriseConfig?.reportRetentionDays ?? DEFAULT_RETENTION

  const [sections, setSections] =
    useState<customizeReportSchemaType>(initialSections)
  const [retentionDays, setRetentionDays] = useState<number>(initialRetention)
  const [sheetOpen, setSheetOpen] = useState(false)

  const [updateUserPdfConfig, { isLoading: isLoadingUpdateUserPdfConfig }] =
    useUpdateUserPdfConfigMutation()
  const [
    updateEnterpriseConfig,
    { isLoading: isLoadingUpdateEnterpriseConfig },
  ] = useUpdateEnterpriseConfigMutation()

  const sectionsDirty =
    JSON.stringify(sections) !== JSON.stringify(initialSections)
  const retentionDirty = retentionDays !== initialRetention
  const isSaveDisabled = !sectionsDirty && !retentionDirty

  const onSave = async () => {
    try {
      await updateUserPdfConfig({
        ...sections,
        pdfFocusedTopics: false,
      }).unwrap()

      if (canEditRetention && retentionDirty) {
        await updateEnterpriseConfig({
          reportRetentionDays: retentionDays,
        }).unwrap()
      }

      toast.success(t('feedback.toast.pdfUpdated'))
    } catch (error) {
      toast.error(t('feedback.toast.errorSavingChanges'))
      catchWithSentry(error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LottieSpinner width={120} height={120} />
      </div>
    )
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <div className="flex flex-1 min-h-0 flex-col gap-8 overflow-y-auto">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-medium text-neutralColor">
            {t('dashboard.account.reportSettings.title')}
          </h2>
          <p className="text-sm text-neutralColor60">
            {t('dashboard.account.reportSettings.intro')}
          </p>
        </div>

        <section className="flex flex-col gap-2">
          <h3 className="text-xl font-medium text-neutralColor">
            {t('dashboard.account.reportSettings.language')}
          </h3>
          <p className="text-sm text-neutralColor60">
            {t('dashboard.account.reportSettings.reportingLanguageText')}
          </p>
          <ToggleGroup
            type="single"
            value={sections.pdfLanguage}
            onValueChange={(value) => {
              if (!value) return
              setSections((prev) => ({ ...prev, pdfLanguage: value }))
            }}
            className="pt-1"
          >
            {LANGUAGES.map(({ code, labelKey }) => (
              <ToggleGroupItem key={code} value={code}>
                {t(labelKey)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </section>

        {canEditRetention && (
          <section className="flex flex-col gap-2">
            <h3 className="text-xl font-medium text-neutralColor">
              {t('dashboard.account.reportSettings.autoArchiveDuration')}
            </h3>
            <p className="text-sm text-neutralColor60">
              {t('dashboard.account.reportSettings.autoArchiveDurationText')}
            </p>
            <ToggleGroup
              type="single"
              value={String(retentionDays)}
              onValueChange={(value) => {
                if (!value) return
                setRetentionDays(Number(value))
              }}
              className="pt-1"
            >
              {RETENTION_OPTIONS.map((days) => (
                <ToggleGroupItem key={days} value={String(days)}>
                  {`${days} ${t('dashboard.account.reportSettings.days')}`}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </section>
        )}

        <section className="flex flex-col items-start gap-3">
          <h3 className="text-xl font-medium text-neutralColor">
            {t('dashboard.account.reportSettings.pDFReportSections')}
          </h3>
          <p className="text-sm text-neutralColor60">
            {t('dashboard.account.reportSettings.pDFReportSectionsText')}
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setSheetOpen(true)}
          >
            {t('dashboard.account.reportSettings.customize')}
          </Button>
        </section>
      </div>

      <div className="flex shrink-0 justify-end pt-6">
        <Button
          type="button"
          disabled={isSaveDisabled}
          isLoading={
            isLoadingUpdateUserPdfConfig || isLoadingUpdateEnterpriseConfig
          }
          onClick={onSave}
          className="self-end"
        >
          {t('dashboard.account.reportSettings.save')}
        </Button>
      </div>

      <CustomizeReportSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        value={sections}
        onSave={setSections}
        hideLanguageSection
      />
    </div>
  )
}
